/**
 * Custom Sounds Hooks
 * React Query hooks for custom notification sound management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCustomSounds,
  uploadCustomSound,
  deleteCustomSound,
} from '@/services/custom-sound.service';
import { useAuthStore } from '@/store/auth.store';

/**
 * Get user's custom sounds
 */
export const useCustomSounds = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: ['customSounds'],
    queryFn: getCustomSounds,
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: isAuthenticated,
  });
};

/**
 * Upload a custom sound
 */
export const useUploadCustomSound = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, name }: { file: File; name: string }) =>
      uploadCustomSound(file, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customSounds'] });
    },
  });
};

/**
 * Delete a custom sound
 */
export const useDeleteCustomSound = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (soundId: string) => deleteCustomSound(soundId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customSounds'] });
      // Settings may reset if deleted sound was active
      queryClient.invalidateQueries({ queryKey: ['userSettings'] });
    },
  });
};
